// The primary pointer, 'coarse' (touch) or 'fine' (mouse, trackpad), from matchMedia('(pointer: coarse)')
// (JDEV-3208). The editor tabs (components/ui/EditorTabs.vue) are a swipeable scroll strip on 'coarse' and a
// one-row strip with the "Lisää (n)" menu on 'fine'.
//
// `pointer`, not `any-pointer`: a touch laptop or 2-in-1 is driven by its trackpad and keeps the menu, which is
// touch-safe anyway (44px rows); only a touch-primary device gets the strip that has no buttons to scroll it.
// The value follows the query's change event (DevTools device mode, a 2-in-1 detached into tablet mode), so a
// strip rebuilds live without a reload.
//
// SSR: the server has no pointer and renders 'fine'. On the client the query is read synchronously in setup,
// so a component mounted after the app has started (every editor and modal) renders the right mode in its first
// paint. Only while Nuxt hydrates server-rendered markup does it start at 'fine' too (a different first render
// would be a hydration mismatch) and switch in onMounted, before the browser has had a frame to show the
// difference in most cases. The CSS side of the switch is @media (pointer: coarse) in the shared tabs.css.
// One media query listener is shared by every caller.

import { computed, onMounted, ref, type ComputedRef } from 'vue'

export type PointerMode = 'coarse' | 'fine'

export const COARSE_POINTER_QUERY = '(pointer: coarse)'

const mode = ref<PointerMode>('fine')
let mql: MediaQueryList | null = null

function listen () {
  if (mql || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
  mql = window.matchMedia(COARSE_POINTER_QUERY)
  mql.addEventListener('change', () => { mode.value = mql?.matches ? 'coarse' : 'fine' })
}

export function usePointerMode (): ComputedRef<PointerMode> {
  if (import.meta.server) return computed(() => 'fine')
  const ready = ref(!useNuxtApp().isHydrating)
  if (ready.value) listen()
  if (mql) mode.value = mql.matches ? 'coarse' : 'fine'
  onMounted(() => {
    listen()
    if (mql) mode.value = mql.matches ? 'coarse' : 'fine'
    ready.value = true
  })
  return computed(() => (ready.value ? mode.value : 'fine'))
}
